/** Ellenség-állapotgép: észlelés, üldözés, telegráfos támadás és dropok. */

import * as THREE from 'three'
import type { Actor, Ctx, System } from '../core/contracts'
import { angleDelta, turnToward } from '../core/math'
import { ENEMIES, PROJECTILES, type EnemyArchetype } from '../content/enemies'

type EnemyMode = 'idle' | 'alert' | 'chase' | 'strafe' | 'attack' | 'pain' | 'death'

interface EnemyState {
  mode: EnemyMode
  timer: number
  cooldown: number
  lastSeenTick: number
  lastX: number
  lastY: number
  lastZ: number
  strafeSign: number
  burstLeft: number
  burstTimer: number
  /** A hound távoli támadásakor a telegráf alatt előrerohan. */
  lunging: boolean
  dropped: boolean
  /** A pályaadatból érkező encounter-csoport (bekötés után). */
  group?: string
}

const _eye = new THREE.Vector3()
const _targetEye = new THREE.Vector3()
const _dir = new THREE.Vector3()
const _side = new THREE.Vector3()
const _up = new THREE.Vector3()
const _probe = new THREE.Vector3()

export class EnemySystem implements System {
  readonly name = 'enemies'
  private readonly enemies = new Set<number>()
  private readonly unsubscribers: (() => void)[] = []

  init(ctx: Ctx): void {
    for (const actor of ctx.actors.all()) if (actor.kind === 'enemy') this.register(actor)
    this.unsubscribers.push(ctx.events.on('spawn', ({ actor }) => {
      if (actor.kind === 'enemy') this.register(actor)
    }))
    this.unsubscribers.push(ctx.events.on('damage', ({ target }) => {
      if (target.kind !== 'enemy' || target.health <= 0) return
      const arch = ENEMIES[target.type]
      const state = this.state(target)
      this.alertGroup(ctx, target, state.group)
      // A találati pillanatban már elindított lövés nem szakítható meg.
      if (state.mode !== 'attack' && ctx.rng.chance(arch.painChance)) {
        state.mode = 'pain'
        state.timer = arch.painDuration
        target.anim.pain = 1
        ctx.fx.sound(arch.sounds.pain, target.pos, 0.55)
      }
    }))
    this.unsubscribers.push(ctx.events.on('death', ({ actor }) => {
      if (actor.kind !== 'enemy') return
      const state = this.state(actor)
      state.mode = 'death'
      actor.anim.telegraph = 0
      if (!state.dropped) this.drop(ctx, actor, state)
    }))
  }

  dispose(): void {
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe()
    this.enemies.clear()
  }

  fixedUpdate(ctx: Ctx): void {
    for (const id of [...this.enemies]) {
      const actor = ctx.actors.get(id)
      if (!actor) {
        this.enemies.delete(id)
        continue
      }
      const arch = ENEMIES[actor.type]
      if (!arch) continue
      const state = this.state(actor)
      if (!actor.alive) {
        actor.anim.telegraph = 0
        continue
      }
      this.updateEnemy(ctx, actor, arch, state)
    }
  }

  private register(actor: Actor): void {
    this.enemies.add(actor.id)
    this.ensureState(actor)
  }

  private ensureState(actor: Actor): void {
    if (!this.isState(actor.data)) {
      const initialGroup = this.groupFromSpawnData(actor.data)
      actor.data = {
        mode: 'idle', timer: 0, cooldown: 0, lastSeenTick: -Infinity,
        lastX: actor.pos.x, lastY: actor.pos.y + actor.eyeHeight, lastZ: actor.pos.z,
        strafeSign: 1, burstLeft: 0, burstTimer: 0, lunging: false, dropped: false, group: initialGroup,
      } satisfies EnemyState
    }
  }

  private state(actor: Actor): EnemyState {
    this.ensureState(actor)
    return actor.data as EnemyState
  }

  private isState(value: unknown): value is EnemyState {
    return typeof value === 'object' && value !== null && 'mode' in value && 'lastSeenTick' in value
  }

  private groupFromSpawnData(value: unknown): string | undefined {
    if (typeof value !== 'object' || value === null || !('group' in value)) return undefined
    const group = value.group
    return typeof group === 'string' ? group : undefined
  }

  private updateEnemy(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    if (state.cooldown > 0) state.cooldown--
    if (state.burstTimer > 0) state.burstTimer--
    actor.anim.telegraph = 0

    const seesPlayer = this.canSee(ctx, actor, arch)
    if (seesPlayer) this.remember(ctx, actor, state)
    const targetKnown = ctx.tick - state.lastSeenTick <= 60 * 5

    switch (state.mode) {
      case 'idle':
        if (seesPlayer) this.enterAlert(ctx, actor, arch, state)
        break
      case 'alert':
        this.turnTo(actor, state.lastX, state.lastZ, 5.4 * ctx.dt)
        if (!targetKnown && state.timer <= 0) state.mode = 'idle'
        else if (--state.timer <= 0 && targetKnown) state.mode = 'chase'
        break
      case 'chase':
        if (!targetKnown) {
          state.mode = 'idle'
          break
        }
        this.turnTo(actor, state.lastX, state.lastZ, 5.2 * ctx.dt)
        if (seesPlayer && this.canAttack(ctx, actor, arch, state)) {
          this.beginAttack(ctx, actor, arch, state)
        } else if (seesPlayer && arch.strafeChance > 0 && this.inStrafeBand(ctx, actor, arch) && ctx.rng.chance(0.035)) {
          this.beginStrafe(actor, arch, state, ctx)
        } else {
          // A tűzvető nem alakulhat át közeli, kvázi hitscan ellenféllé:
          // a minimum hatótávján belül előbb távolságot kér, csak utána lő.
          if (seesPlayer && this.isTooClose(ctx, actor, arch)) this.moveAway(ctx, actor, arch, state.lastX, state.lastZ)
          else this.moveToward(ctx, actor, arch, state.lastX, state.lastZ)
        }
        break
      case 'strafe':
        this.turnTo(actor, state.lastX, state.lastZ, 6 * ctx.dt)
        if (seesPlayer && this.canAttack(ctx, actor, arch, state)) {
          this.beginAttack(ctx, actor, arch, state)
          break
        }
        this.moveSideways(ctx, actor, arch, state)
        if (--state.timer <= 0 || !seesPlayer || !this.inStrafeBand(ctx, actor, arch)) state.mode = 'chase'
        break
      case 'attack':
        this.turnTo(actor, state.lastX, state.lastZ, 7 * ctx.dt)
        this.attackTick(ctx, actor, arch, state)
        break
      case 'pain':
        actor.anim.pain = 1
        if (--state.timer <= 0) state.mode = targetKnown ? 'chase' : 'alert'
        break
      case 'death':
        break
    }

    actor.pos.y = ctx.world.floorAt(actor.pos.x, actor.pos.z, actor.roomId)
  }

  private canSee(ctx: Ctx, actor: Actor, arch: EnemyArchetype): boolean {
    const p = ctx.actors.player
    const dx = p.pos.x - actor.pos.x
    const dz = p.pos.z - actor.pos.z
    const distance = Math.hypot(dx, dz)
    if (distance > arch.sightRange) return false
    const yaw = Math.atan2(dx, dz)
    if (Math.abs(angleDelta(actor.yaw, yaw)) > arch.sightHalfFov) return false
    _eye.set(actor.pos.x, actor.pos.y + actor.eyeHeight, actor.pos.z)
    _targetEye.set(p.pos.x, p.pos.y + p.eyeHeight, p.pos.z)
    return ctx.world.lineOfSight(_eye, _targetEye)
  }

  private remember(ctx: Ctx, actor: Actor, state: EnemyState): void {
    const p = ctx.actors.player
    state.lastSeenTick = ctx.tick
    state.lastX = p.pos.x
    state.lastY = p.pos.y + p.eyeHeight
    state.lastZ = p.pos.z
    void actor
  }

  private enterAlert(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    state.mode = 'alert'
    // Az első észlelés nem lehet azonnali találat: a telegráf előtt is
    // marad egy típusonként hangolt reakcióablak.
    state.timer = Math.max(15, arch.attack.firstSightDelay)
    ctx.fx.sound(arch.sounds.sight, actor.pos, 0.7)
    this.alertGroup(ctx, actor, state.group)
  }

  private alertGroup(ctx: Ctx, source: Actor, group?: string): void {
    // Csoportazonosító nélkül nem találgatunk: a pályaszerző a `data.group`-ba adja át.
    if (!group) return
    for (const other of ctx.actors.all()) {
      if (other.kind !== 'enemy' || !other.alive || other.id === source.id) continue
      const state = this.state(other)
      if (state.group !== group || state.mode !== 'idle') continue
      this.remember(ctx, other, state)
      state.mode = 'alert'
      // A csoport első tüze determinisztikusan elkenődik; nem érkezik egy
      // tickben kivédhetetlen sortűz.
      state.timer = Math.max(15, ENEMIES[other.type].attack.firstSightDelay + ctx.rng.int(0, ENEMIES[other.type].attack.cooldown))
    }
  }

  private canAttack(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): boolean {
    if (state.cooldown > 0) return false
    const p = ctx.actors.player
    const distance = Math.hypot(p.pos.x - actor.pos.x, p.pos.z - actor.pos.z)
    const inNormalRange = distance >= (arch.attack.minRange ?? 0) && distance <= arch.attack.range
    const inHoundLungeRange = arch.id === 'hound' && distance >= 2.5 && distance <= 5
    return (inNormalRange || inHoundLungeRange) && ctx.world.lineOfSight(
      _eye.set(actor.pos.x, actor.pos.y + actor.eyeHeight, actor.pos.z),
      _targetEye.set(p.pos.x, p.pos.y + p.eyeHeight, p.pos.z),
    )
  }

  private beginAttack(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    state.mode = 'attack'
    state.timer = arch.attack.telegraph
    state.burstLeft = arch.attack.shots
    state.burstTimer = 0
    const distance = Math.hypot(state.lastX - actor.pos.x, state.lastZ - actor.pos.z)
    state.lunging = arch.id === 'hound' && distance > arch.attack.range
    ctx.fx.sound('telegraph', actor.pos, 0.5, this.telegraphPitch(arch.id))
  }

  private attackTick(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    if (state.timer > 0) {
      actor.anim.telegraph = 1 - state.timer / arch.attack.telegraph
      if (state.lunging) this.moveToward(ctx, actor, arch, state.lastX, state.lastZ, arch.attack.lungeSpeed ?? arch.speed)
      state.timer--
      return
    }
    if (state.burstTimer > 0) return
    // A telegráf alatt a játékos ránk futhat. Ilyenkor ne süssük el a
    // tűzgolyót az arcába: a támadás megszakad, az imp a chase állapotban
    // hátralép, és csak ismét kikerülhető távolságból próbálkozik újra.
    if (this.isTooClose(ctx, actor, arch)) {
      state.lunging = false
      state.mode = 'chase'
      return
    }
    this.performAttack(ctx, actor, arch, state)
    state.burstLeft--
    if (state.burstLeft > 0) {
      state.burstTimer = arch.attack.shotInterval
      return
    }
    state.cooldown = arch.attack.cooldown
    state.lunging = false
    if (arch.id !== 'brute' && ctx.rng.chance(arch.strafeChance)) this.beginStrafe(actor, arch, state, ctx)
    else state.mode = 'chase'
  }

  private performAttack(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    const attack = arch.attack
    _eye.set(actor.pos.x, actor.pos.y + actor.eyeHeight, actor.pos.z)
    _targetEye.set(state.lastX, state.lastY, state.lastZ)
    _dir.copy(_targetEye).sub(_eye).normalize()
    const player = ctx.actors.player
    const hasCurrentLineOfSight = ctx.world.lineOfSight(
      _eye,
      _targetEye.set(player.pos.x, player.pos.y + player.eyeHeight, player.pos.z),
    )
    // A lövés az utolsó ismert pontra indul, de a játékos nem sérülhet meg,
    // ha a telegráf alatt már fedezékbe ért.
    _targetEye.set(state.lastX, state.lastY, state.lastZ)
    ctx.fx.sound(arch.sounds.attack, actor.pos, 0.55)
    if (attack.kind === 'melee') {
      if (!hasCurrentLineOfSight) return
      const wall = ctx.world.raycast(_eye, _dir, attack.range)
      const hit = ctx.actors.raycast(_eye, _dir, wall?.distance ?? attack.range, actor, 'player')
      if (hit) ctx.actors.damage(hit.actor, ctx.rng.damage(...attack.damage), actor, _dir.clone(), attack.damageKind)
      return
    }
    if (attack.kind === 'hitscan') {
      const spread = Math.tan(attack.spreadDeg * Math.PI / 180)
      _side.crossVectors(_dir, THREE.Object3D.DEFAULT_UP).normalize()
      if (_side.lengthSq() < 1e-6) _side.set(1, 0, 0)
      _up.crossVectors(_side, _dir).normalize()
      if (attack.accuracy !== undefined && !ctx.rng.chance(attack.accuracy)) {
        // A hibázás a célpont síkjában 0,8–1,6 egység: távolságtól
        // függetlenül valódi mellélövés, közelről sem geometriai álhiba.
        const missOffset = ctx.rng.range(0.8, 1.6)
        const angle = ctx.rng.next() * Math.PI * 2
        _targetEye.addScaledVector(_side, Math.cos(angle) * missOffset)
          .addScaledVector(_up, Math.sin(angle) * missOffset)
        _dir.copy(_targetEye).sub(_eye).normalize()
      } else {
        _dir.addScaledVector(_side, ctx.rng.spread(spread)).addScaledVector(_up, ctx.rng.spread(spread)).normalize()
      }
      const wall = ctx.world.raycast(_eye, _dir, attack.range)
      if (!hasCurrentLineOfSight) {
        if (wall) {
          ctx.fx.impact(wall.point, wall.normal, wall.impact)
          ctx.fx.tracer(_eye, wall.point, 0xff593d)
        }
        return
      }
      const hit = ctx.actors.raycast(_eye, _dir, wall?.distance ?? attack.range, actor, 'player')
      if (hit) {
        ctx.actors.damage(hit.actor, ctx.rng.damage(...attack.damage), actor, _dir.clone(), attack.damageKind)
        ctx.fx.tracer(_eye, hit.point, 0xff593d)
      } else if (wall) {
        ctx.fx.impact(wall.point, wall.normal, wall.impact)
        ctx.fx.tracer(_eye, wall.point, 0xff593d)
      }
      return
    }
    const projectile = PROJECTILES[attack.projectile!]
    const bolt = ctx.actors.spawn({
      kind: 'projectile', type: projectile.id, faction: 'enemy',
      x: _eye.x + _dir.x * (actor.radius + projectile.radius + 0.1), y: _eye.y + _dir.y * (actor.radius + projectile.radius + 0.1), z: _eye.z + _dir.z * (actor.radius + projectile.radius + 0.1),
      radius: projectile.radius, height: projectile.radius * 2, solid: false, vulnerable: false,
      data: {
        ownerId: actor.id, damage: ctx.rng.damage(...attack.damage), damageKind: attack.damageKind,
        splashRadius: projectile.splashRadius, splashDamage: projectile.splashDamage,
        lifetime: projectile.lifetime, color: projectile.color,
      },
    })
    bolt.vel.copy(_dir).multiplyScalar(projectile.speed)
    bolt.roomId = actor.roomId
  }

  private beginStrafe(actor: Actor, arch: EnemyArchetype, state: EnemyState, ctx: Ctx): void {
    state.mode = 'strafe'
    state.timer = arch.strafeDuration
    state.strafeSign = ctx.rng.chance(0.5) ? 1 : -1
    void actor
  }

  private moveToward(ctx: Ctx, actor: Actor, arch: EnemyArchetype, x: number, z: number, speed = arch.speed): void {
    _dir.set(x - actor.pos.x, 0, z - actor.pos.z)
    if (_dir.lengthSq() < 0.04) return
    _dir.normalize()
    const step = speed * ctx.dt
    const nx = actor.pos.x + _dir.x * step
    const nz = actor.pos.z + _dir.z * step
    if (ctx.world.canWalk(actor.pos, _probe.set(nx, actor.pos.y, nz), actor.radius, 0.45)) {
      ctx.world.moveActor(actor, _dir.x * step, _dir.z * step, 0.45)
      return
    }
    // A pályák nem labirintusok: az egyik oldalra kitérés elég a legtöbb sarokhoz.
    _side.set(_dir.z, 0, -_dir.x)
    const sign = (actor.id & 1) === 0 ? 1 : -1
    ctx.world.moveActor(actor, _side.x * step * sign, _side.z * step * sign, 0.45)
  }

  private moveAway(ctx: Ctx, actor: Actor, arch: EnemyArchetype, x: number, z: number): void {
    _dir.set(actor.pos.x - x, 0, actor.pos.z - z)
    if (_dir.lengthSq() < 0.04) return
    _dir.normalize()
    const step = arch.speed * ctx.dt
    const nx = actor.pos.x + _dir.x * step
    const nz = actor.pos.z + _dir.z * step
    if (ctx.world.canWalk(actor.pos, _probe.set(nx, actor.pos.y, nz), actor.radius, 0.45)) {
      ctx.world.moveActor(actor, _dir.x * step, _dir.z * step, 0.45)
      return
    }
    // Sarokban legalább oldalra próbál kitérni, ahelyett hogy a játékosba
    // sétálna és ismét közelről készítene elő lövést.
    _side.set(_dir.z, 0, -_dir.x)
    const sign = (actor.id & 1) === 0 ? 1 : -1
    ctx.world.moveActor(actor, _side.x * step * sign, _side.z * step * sign, 0.45)
  }

  private isTooClose(ctx: Ctx, actor: Actor, arch: EnemyArchetype): boolean {
    const minRange = arch.attack.minRange
    if (minRange === undefined) return false
    const player = ctx.actors.player
    return Math.hypot(player.pos.x - actor.pos.x, player.pos.z - actor.pos.z) < minRange
  }

  private moveSideways(ctx: Ctx, actor: Actor, arch: EnemyArchetype, state: EnemyState): void {
    _side.set(-Math.cos(actor.yaw), 0, Math.sin(actor.yaw)).multiplyScalar(state.strafeSign * arch.speed * ctx.dt)
    if (ctx.world.canWalk(actor.pos, _probe.set(actor.pos.x + _side.x, actor.pos.y, actor.pos.z + _side.z), actor.radius, 0.45)) ctx.world.moveActor(actor, _side.x, _side.z, 0.45)
    else state.timer = 0
  }

  private inStrafeBand(ctx: Ctx, actor: Actor, arch: EnemyArchetype): boolean {
    const p = ctx.actors.player
    const distance = Math.hypot(p.pos.x - actor.pos.x, p.pos.z - actor.pos.z)
    return distance >= arch.attack.range * 0.6 && distance <= arch.attack.range
  }

  private turnTo(actor: Actor, x: number, z: number, maxStep: number): void {
    actor.yaw = turnToward(actor.yaw, Math.atan2(x - actor.pos.x, z - actor.pos.z), maxStep)
  }

  private telegraphPitch(enemyId: string): number {
    switch (enemyId) {
      case 'brute': return 0.72
      case 'hound': return 1.28
      case 'imp': return 1.08
      case 'sentinel': return 0.92
      default: return 1
    }
  }

  private drop(ctx: Ctx, actor: Actor, state: EnemyState): void {
    state.dropped = true
    const arch = ENEMIES[actor.type]
    for (const drop of arch.drops) {
      if (!ctx.rng.chance(drop.chance)) continue
      const pickup = ctx.actors.spawn({
        kind: 'pickup', type: drop.type, faction: 'neutral', x: actor.pos.x, y: actor.pos.y, z: actor.pos.z,
        radius: 0.35, height: 0.7, solid: false, vulnerable: false,
      })
      pickup.roomId = actor.roomId
    }
  }
}
